/*
<summary>
   This class component is all about Managing Tool BOM data.
   Developer: Aashish Singh, Created Date: 07-Sep-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/

import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import toast from 'react-hot-toast';
import URLConstants from '../../constants/url-constants';
import {
  IAddToolBOM,
  IAddToolBOMRequest,
  IUpdateToolBOMRequest,
} from '../../models/forms/IToolBOM';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import {
  IToolBOMCSVImportResult,
  IToolBOMInfo,
  IToolBOMList,
  IToolBOMListResponseVM,
  IToolBomRelationInfo,
  IToolBomToolManagementInfo,
  IToolBomUsageInfoDetail,
  IValidateBomToolStatusResult,
  ToolBomUsageModeEnum,
  ToolBomUsageStateEnum,
} from '../../models/response/IToolBOMResponse';
import { formatMessage } from '../../translations/format-message';
import {
  AddToolBOMInitialState,
  getToolBOMListInitialState,
  ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';
import {
  getToolBOMFactoryIdFromLocalStorage,
  setToolBOMFactoryIdToLocalStorage,
  getUserId,
  getTenantIdFromLocalStorage,
} from '../../helpers/localstorage-helper';
import { downloadCsvFromRows } from './utils/download-csv';

const SCAN_ERROR_KEY_MAP: Record<string, string> = {
  ToolScanDisposed: 'tool_scan_disposed',
  ToolScanCalibrationExpired: 'tool_scan_calibration_expired',
  ToolScanNotInspected: 'tool_scan_not_inspected',
  ToolScanInUse: 'tool_scan_in_use',
  ToolScanOnLoan: 'tool_scan_on_loan',
  ToolScanUnavailable: 'tool_scan_unavailable',
  ToolScanUnusable: 'tool_scan_unusable',
  ToolScanProcessNotUsable: 'tool_scan_process_not_usable',
  ApplicatorNotMounted: 'applicator_not_mounted',
  MainUnitNotInAccessedFactory: 'main_unit_not_in_accessed_factory',
  MainUnitScanDisposed: 'main_unit_scan_disposed',
  MainUnitScanCalibrationExpired: 'main_unit_scan_calibration_expired',
  MainUnitScanNotInspected: 'main_unit_scan_not_inspected',
  MainUnitScanInUse: 'main_unit_scan_in_use',
  MainUnitScanOnLoan: 'main_unit_scan_on_loan',
  MainUnitScanUnavailable: 'main_unit_scan_unavailable',
  MainUnitScanUnusable: 'main_unit_scan_unusable',
  MainUnitScanProcessNotUsable: 'main_unit_scan_process_not_usable',
  NoRecordFound: 'tool_not_found',
  ToolManagementNoRequired: 'ToolManagementNoRequired',
};

export interface IScanToolResult {
  success: boolean;
  // Translation key for the inline error message the Tool Scan popup should render below the
  // input - undefined when success is true, or when the scanned value was empty.
  errorKey?: string;
  errorMessage?: string;
}

export class ToolBOMStore {
  initialStateValue = ObservableInitialState;
  toolBOMList: IToolBOMListResponseVM = getToolBOMListInitialState;
  addUpdateToolBOMState = { ...this.initialStateValue };
  deleteToolBOMState = { ...this.initialStateValue };
  getAllToolBOMState = { ...this.initialStateValue };
  toolBOMDetail: IAddToolBOM = AddToolBOMInitialState;
  getToolBOMByIdState = { ...this.initialStateValue };
  getFactoryIdState = { ...this.initialStateValue };
  fileUploadState = { ...this.initialStateValue };
  importResults: IToolBOMCSVImportResult[] = [];

  // Operator Assign Task "Tool BOM" tab - left table (Material/Tool relation list).
  toolBomRelationInfoList: IToolBomRelationInfo[] = [];
  getToolBomRelationInfoState = { ...this.initialStateValue };

  // Operator Assign Task "Tool BOM" tab - right table (Tool management list), populated per
  // left-table row via its Get button. No real API exists yet, so this is served from mock data.
  toolBomToolManagementList: IToolBomToolManagementInfo[] = [];
  getToolBomToolManagementListState = { ...this.initialStateValue };
  // The ToolKatashiki/ToolModel the currently-loaded toolBomToolManagementList was fetched for
  // (the left table row's Get button click) - carried along as ToolModel on every scanned tool's
  // ToolBomUsageInfo payload, since all of them came from this same target tool list.
  selectedToolModel: string = '';
  private currentProcessName = '';

  // Operator Assign Task "Tool BOM" tab - the Tool Scan popup's scanned tool list. Only tools
  // present in toolBomToolManagementList (the target tool list) may be added here.
  scannedToolNumbers: string[] = [];
  // Main Unit tool numbers auto-added to scannedToolNumbers because an Applicator mounted on
  // them was scanned - tracked so the UI can tag them "(M)" and so
  // removing the Applicator can cascade-remove its Main Unit too.
  scannedToolMainUnitNumbers: string[] = [];
  // Applicator tool number - the Main Unit tool number it auto-added, for the cascade removal
  // above. Not rendered directly, so it doesn't need to be observable.
  private applicatorMainUnitMap: Record<string, string> = {};
  // True while the Tool Scan popup is open
  isToolScanPopupOpen: boolean = false;
  addToolBomUsageInfoState = { ...this.initialStateValue };
  // Not observable - request counter for GetToolBomToolManagementListService.
  private toolManagementListRequestId = 0;
  updateToolStatusByToolManagementNoState = { ...this.initialStateValue };
  getToolMgtNumberByTrayState = { ...this.initialStateValue };
  releaseToolState = { ...this.initialStateValue };

  sentScannedToolNumbers: string[] = [];
  allSessionScannedToolNumbers: string[] = [];
  lastTrayScannedToolNumbers: string[] = [];
  keepFactoryId: string = '';
  keepToolModel: string = '';
  // Single mode, multi-tray: scannedToolNumbers/sentScannedToolNumbers always belong to
  // activeTrayId. When the operator switches to a different tray (not the Keep Tool flow), the
  // tray being left is stashed here by tray id and the target tray's own stash (if any) is
  // loaded, so one tray's tools are never sent along with another tray.
  activeTrayId: string = '';
  trayToolsMap: Record<string, { scanned: string[]; sent: string[] }> = {};
  // Batch mode: tools already recorded against each scanned tray (from an earlier run in any mode,
  // e.g. the tray was paused by someone) - kept apart from scannedToolNumbers so they're never
  // crossed with the other trays' rows, only sent with their own tray on Pause/Complete.
  batchTrayPriorTools: Record<string, string[]> = {};

  constructor() {
    makeObservable(this, {
      toolBOMList: observable,
      addUpdateToolBOMState: observable,
      deleteToolBOMState: observable,
      getAllToolBOMState: observable,
      toolBOMDetail: observable,
      getToolBOMByIdState: observable,
      getFactoryIdState: observable,
      fileUploadState: observable,
      importResults: observable,
      toolBomRelationInfoList: observable,
      getToolBomRelationInfoState: observable,
      toolBomToolManagementList: observable,
      getToolBomToolManagementListState: observable,
      selectedToolModel: observable,
      scannedToolNumbers: observable,
      scannedToolMainUnitNumbers: observable,
      isToolScanPopupOpen: observable,
      setToolScanPopupOpen: action,
      setCurrentProcessName: action,
      sentScannedToolNumbers: observable,
      allSessionScannedToolNumbers: observable,
      lastTrayScannedToolNumbers: observable,
      keepFactoryId: observable,
      keepToolModel: observable,
      activeTrayId: observable,
      trayToolsMap: observable,
      batchTrayPriorTools: observable,
      FetchBatchTrayPriorTools: action,
      addToolBomUsageInfoState: observable,
      updateToolStatusByToolManagementNoState: observable,
      getToolMgtNumberByTrayState: observable,
      releaseToolState: observable,
      UpdateToolStatusByToolManagementNoService: action,
      GetToolMgtNumberByTrayAndProceesIdService: action,
      mergeScannedTools: action,
      mergeTrayTools: action,
      switchTrayTools: action,
      removeTrayTools: action,
      markToolsSent: action,
      unmarkToolsSent: action,
      ReportIncrementalToolStart: action,
      snapshotForKeep: action,
      resetForNextTray: action,
      resetSessionToolTracking: action,
      GetFactoryIdByTenantIdService: action,
      GetToolBOMListService: action,
      AddToolBOMService: action,
      UpdateToolBOMService: action,
      DeleteToolBOMService: action,
      GetToolBOMByIdService: action,
      ImportToolBOMFileUploadService: action,
      GetToolBomRelationInfoService: action,
      GetToolBomToolManagementListService: action,
      ToolBomUsageInfoService: action,
      ReportToolBomUsage: action,
      addScannedTool: action,
      removeScannedTool: action,
      resetScannedTools: action,
      ReleaseToolService: action,
      resetAddUpdateState: action,
      resetDeleteState: action,
      resetDetail: action,
      resetFileUploadState: action,
      resetToolBomRelationInfo: action,
      resetToolBomToolManagementList: action,
      getToolBOMList: computed,
      resetStore: action,
      resetToolBOMList: action,
    });
  }

  /**
  * This function is used to resolve a tenant's factory_id (the tenant's actual database name)
  * from its numeric tenant id, and cache it in local storage for the Tool BOM module's CRUD
  * calls to use for as long as the user stays on the module.
  * @param tenantId: Numeric tenant identifier (from oms_access_tenant_id)
  */
  GetFactoryIdByTenantIdService = (tenantId: number) => {
    this.getFactoryIdState.inProgress = true;
    const url = `${URLConstants.GetFactoryIdByTenantId}?tenantId=${tenantId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<string>>) => {
        if (response.data.Error) {
          this.getFactoryIdState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.getFactoryIdState.success = true;
          setToolBOMFactoryIdToLocalStorage(response.data.Data || '');
        }
      })
      .catch((err: any) => {
        this.getFactoryIdState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.getFactoryIdState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to get the Tool BOM list from API by page number.
  * @param currentPage: Current page number
  * @param pageLimit: Number of records per page
  */
  GetToolBOMListService = (currentPage: number, pageLimit: number) => {
    this.getAllToolBOMState.inProgress = true;
    const factoryId = getToolBOMFactoryIdFromLocalStorage();
    const url = `${URLConstants.GetToolBOMInfoList}?PageNo=${currentPage}&PageSize=${pageLimit}&factoryId=${encodeURIComponent(factoryId || '')}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IToolBOMListResponseVM>>) => {
        if (response.data.Error) {
          this.getAllToolBOMState.error = response.data.Message;
          // toast.error(formatMessage(response.data.Message));
        } else {
          this.toolBOMList = response.data.Data;
        }
      })
      .catch((err: any) => {
        this.getAllToolBOMState.error = err;
        // toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.getAllToolBOMState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to add a new Tool BOM record.
  * @param data: Tool BOM details to be added
  */
  AddToolBOMService = (data: IAddToolBOMRequest) => {
    this.addUpdateToolBOMState.inProgress = true;
    return baseService
      .postRequest(URLConstants.AddToolBOMInfo, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateToolBOMState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.addUpdateToolBOMState.success = true;
          toast.success(formatMessage('added_success'));
        }
      })
      .catch((err: any) => {
        this.addUpdateToolBOMState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.addUpdateToolBOMState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to update an existing Tool BOM record.
  * @param data: Tool BOM details to be updated
  */
  UpdateToolBOMService = (data: IUpdateToolBOMRequest) => {
    this.addUpdateToolBOMState.inProgress = true;
    return baseService
      .putRequest(URLConstants.UpdateToolBOMInfo, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateToolBOMState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.addUpdateToolBOMState.success = true;
          toast.success(formatMessage('updated_success'));
        }
      })
      .catch((err: any) => {
        this.addUpdateToolBOMState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.addUpdateToolBOMState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to delete an existing Tool BOM record.
  * @param id: Tool BOM identifier
  * @param toolName: Tool name, sent along so audit logs show a human-readable name
  * @param tenantId: Tenant Identifier
  */
  DeleteToolBOMService = (id: number, toolName: string, tenantId: number) => {
    this.deleteToolBOMState.inProgress = true;
    const factoryId = getToolBOMFactoryIdFromLocalStorage();
    const url = `${URLConstants.DeleteToolBOMInfo}?id=${id}&toolManagementNo=${encodeURIComponent(toolName || '')}&factoryId=${encodeURIComponent(factoryId || '')}&tenantId=${tenantId}`;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteToolBOMState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.deleteToolBOMState.success = true;
        }
      })
      .catch((err: any) => {
        this.deleteToolBOMState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.deleteToolBOMState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to get a Tool BOM detail by id from API.
  * @param id: Tool BOM identifier
  */
  GetToolBOMByIdService = (id: number) => {
    this.getToolBOMByIdState.inProgress = true;
    const factoryName = getToolBOMFactoryIdFromLocalStorage();
    const url = `${URLConstants.GetToolBOMInfoById}?Id=${id}&factoryName=${encodeURIComponent(factoryName || '')}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IToolBOMInfo>>) => {
        if (response.data.Error) {
          this.getToolBOMByIdState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.toolBOMDetail = this.mapToolBOMDetail(response.data.Data);
        }
      })
      .catch((err: any) => {
        this.getToolBOMByIdState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.getToolBOMByIdState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to upload a CSV file for bulk Tool BOM import.
  * @param factoryId: Tenant's factory_id (DB name) the Tool BOM records belong to
  * @param createdBy: User Identifier who is uploading the file
  * @param csvFile: CSV File to be uploaded
  * @param tenantId: Tenant Identifier
  */
  ImportToolBOMFileUploadService = (factoryId: string, createdBy: number, csvFile: File, tenantId: number) => {
    this.fileUploadState.inProgress = true;
    const url = `${URLConstants.ImportToolBOMCSV}?FactoryId=${encodeURIComponent(factoryId || '')}&CreatedBy=${createdBy}&TenantId=${tenantId}`;
    const formData = new FormData();
    formData.append('csvFile', csvFile);
    return baseService
      .postMultipart(url, formData, {})
      .then((response: IApiResponse<IApiSuccessResponse<IToolBOMCSVImportResult[]>>) => {
        const results = response.data.Data || [];
        this.importResults = results;
        if (results.length > 0) {
          downloadCsvFromRows(
            [
              formatMessage('row_no'),
              formatMessage('tool_management_no'),
              formatMessage('status'),
              formatMessage('message'),
            ],
            results.map((row) => [
              row.RowNo,
              row.ToolManagementNo || '',
              row.Success ? formatMessage('success') : formatMessage('failed'),
              formatMessage(row.Message),
            ]),
            'ToolBOMImportResult'
          );
        }
        if (response.data.Error) {
          this.fileUploadState.error = response.data.Message;
        } else {
          this.fileUploadState.success = true;
        }
      })
      .catch((err: string) => {
        this.fileUploadState.error = err;
      })
      .finally(
        action(() => {
          this.fileUploadState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to get the Material/Tool relation list (Tool BOM tab's left table, on
  * the Operator Assign Task screen) for a factory from the GetToolBomRelationInfo API.
  * @param factoryId: Tenant's factory_id (DB name) to fetch the relation list for
  * @param trayId: Opened tray; only the Tool BOM relations of this tray's materials are returned
  */
  GetToolBomRelationInfoService = (factoryId: string, trayId: string) => {
    this.getToolBomRelationInfoState.inProgress = true;
    const url = `${URLConstants.GetToolBomRelationInfo}?factoryId=${encodeURIComponent(factoryId || '')}&trayId=${encodeURIComponent(trayId || '')}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IToolBomRelationInfo[]>>) => {
        if (response.data.Error) {
          this.getToolBomRelationInfoState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.toolBomRelationInfoList = response.data.Data || [];
          this.getToolBomRelationInfoState.success = true;
        }
      })
      .catch((err: any) => {
        this.getToolBomRelationInfoState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.getToolBomRelationInfoState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to get the Tool management list (Tool BOM tab's "Target Tool List"
  * right table, on the Operator Assign Task screen) for the left-table row whose Get button was
  * clicked, via GetBomToolStatusByToolKatashiki.
  * @param toolKatashiki: Tool Katashiki (ToolModel) of the left-table row the Get button was clicked on
  * @param factoryId: Tenant's factory_id (DB name) the row belongs to
  * @param tenantId: Tenant Identifier
  */
  GetToolBomToolManagementListService = (toolKatashiki: string, factoryId: string, tenantId: number) => {
    this.getToolBomToolManagementListState.inProgress = true;
    const requestId = ++this.toolManagementListRequestId;
    const isLatestRequest = () => requestId === this.toolManagementListRequestId;
    const processNameParam = this.currentProcessName
      ? `&processName=${encodeURIComponent(this.currentProcessName)}`
      : '';
    const url = `${URLConstants.GetBomToolStatusByToolKatashiki}?toolKatashiki=${encodeURIComponent(toolKatashiki || '')}&factoryId=${encodeURIComponent(factoryId || '')}&tenantId=${tenantId}${processNameParam}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IToolBomToolManagementInfo[]>>) => {
        if (!isLatestRequest()) return;
        if (response.data.Error) {
          this.getToolBomToolManagementListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.toolBomToolManagementList = response.data.Data || [];
          this.selectedToolModel = toolKatashiki || '';
          // Deliberately NOT clearing scannedToolNumbers here - the operator can Get a different
          // Material/Tool row (a different target tool list) and the Tool Scan popup still shows
          // everything scanned so far, across every target tool list visited this session.
          this.getToolBomToolManagementListState.success = true;
        }
      })
      .catch((err: any) => {
        if (!isLatestRequest()) return;
        this.getToolBomToolManagementListState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          if (isLatestRequest()) {
            this.getToolBomToolManagementListState.inProgress = false;
          }
        })
      );
  };

  /**
  * This function is used to set the process opened on the Operator Assign Task screen, so the
  * target tool list is filtered to the tools used in that process.
  * @param processName: Opened process name ('' to clear)
  */
  setCurrentProcessName = (processName: string | null) => {
    this.currentProcessName = processName || '';
  };

  setToolScanPopupOpen = (isOpen: boolean) => {
    this.isToolScanPopupOpen = isOpen;
  };
  /**
  * This function is used to add a tool management number to the Tool Scan popup's scanned list.
  * The tool is validated by calling an api.
  * @param toolManagementNo: Tool Management Number read from the scanner
  * @param factoryId: Tenant's factory_id (DB name) to validate the tool against
  * @param tenantId: Numeric tenant identifier
  * @param processName: The process currently being worked
  */
  addScannedTool = (
    toolManagementNo: string,
    factoryId: string,
    tenantId: number,
    processName?: string | null
  ): Promise<IScanToolResult> => {
    const value = String(toolManagementNo || '').trim();
    if (!value) return Promise.resolve({ success: false });

    if (this.scannedToolNumbers.includes(value)) {
      return Promise.resolve({ success: false, errorKey: 'tool_already_scanned' });
    }

    const url = `${URLConstants.GetValidateBomToolStatusByTool}?toolManagementNo=${encodeURIComponent(value)}&factoryId=${encodeURIComponent(factoryId || '')}&tenantId=${tenantId}&processName=${encodeURIComponent(processName || '')}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IValidateBomToolStatusResult>>) => {
        if (response.data.Error || !response.data.Data) {
          const mappedKey = SCAN_ERROR_KEY_MAP[response.data.Message];
          return mappedKey
            ? { success: false, errorKey: mappedKey }
            : { success: false, errorMessage: response.data.Message || formatMessage('tool_not_found', { toolNo: value }) };
        }

        const data = response.data.Data;

        runInAction(() => {
          this.scannedToolNumbers = [...this.scannedToolNumbers, data.ToolManagementNo];
          if (!this.allSessionScannedToolNumbers.includes(data.ToolManagementNo)) {
            this.allSessionScannedToolNumbers = [...this.allSessionScannedToolNumbers, data.ToolManagementNo];
          }

          if (data.MainUnitManagementNo && !this.scannedToolNumbers.includes(data.MainUnitManagementNo)) {
            this.scannedToolNumbers = [...this.scannedToolNumbers, data.MainUnitManagementNo];
            this.scannedToolMainUnitNumbers = [...this.scannedToolMainUnitNumbers, data.MainUnitManagementNo];
            this.applicatorMainUnitMap[data.ToolManagementNo] = data.MainUnitManagementNo;
            if (!this.allSessionScannedToolNumbers.includes(data.MainUnitManagementNo)) {
              this.allSessionScannedToolNumbers = [...this.allSessionScannedToolNumbers, data.MainUnitManagementNo];
            }
          }
        });

        return { success: true };
      })
      .catch(() => ({ success: false, errorMessage: formatMessage('SomethingWentWrong') }));
  };

  /**
  * This function is used to remove a tool management number from the Tool Scan popup's scanned
  * list. Removing an Applicator that auto-added its Main Unit also removes the Main Unit, unless
  * another still-scanned Applicator is mounted on that same Main Unit.
  * @param toolManagementNo: Tool Management Number to remove
  */
  removeScannedTool = (toolManagementNo: string) => {
    let numbersToRemove = [toolManagementNo];

    const mainUnitNo = this.applicatorMainUnitMap[toolManagementNo];
    if (mainUnitNo) {
      delete this.applicatorMainUnitMap[toolManagementNo];
      const stillNeeded = Object.values(this.applicatorMainUnitMap).includes(mainUnitNo);
      if (!stillNeeded) {
        numbersToRemove = [...numbersToRemove, mainUnitNo];
        this.scannedToolMainUnitNumbers = this.scannedToolMainUnitNumbers.filter(
          (tool) => tool !== mainUnitNo
        );
      }
    }

    this.scannedToolNumbers = this.scannedToolNumbers.filter(
      (tool) => !numbersToRemove.includes(tool)
    );
  };

  /**
  * This function is used to clear the Tool Scan popup's scanned list.
  */
  resetScannedTools = () => {
    this.scannedToolNumbers = [];
    this.scannedToolMainUnitNumbers = [];
    this.applicatorMainUnitMap = {};
  };

  /**
  * This function is used to release tools.
  * @param toolManagementNos: Tool Management Numbers to release
  * @param trayIds: Trays to report the Release (State 5) against in tool_usage_info, so
  * @param processId: Process Identifier the trays belong to
  * @param mode: Single/Batch
  */
  ReleaseToolService = (
    toolManagementNos: string[],
    trayIds?: string[],
    processId?: number,
    mode: ToolBomUsageModeEnum = ToolBomUsageModeEnum.Single
  ) => {
    this.releaseToolState.error = '';
    this.releaseToolState.success = false;

    const validToolNumbers = (toolManagementNos || []).filter((toolNo) => !!toolNo);
    if (validToolNumbers.length === 0) {
      this.releaseToolState.success = true;
      return Promise.resolve();
    }

    this.releaseToolState.inProgress = true;
    return baseService
      .postRequest(URLConstants.UpdateToolStatusByToolManagementNo, {
        ToolManagementNo: validToolNumbers,
        IsStart: false,
        IsRelease: true,
        ReleaseUsageInfo:
          trayIds && trayIds.length > 0 && processId
            ? {
                TrayIds: trayIds,
                ProcessId: processId,
                FactoryId: getToolBOMFactoryIdFromLocalStorage() || '',
                ToolModel: this.selectedToolModel,
                Mode: mode,
              }
            : null,
      })
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.releaseToolState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.releaseToolState.success = true;
          validToolNumbers.forEach((toolNo) => this.removeScannedTool(toolNo));
          this.sentScannedToolNumbers = this.sentScannedToolNumbers.filter(
            (tool) => !validToolNumbers.includes(tool)
          );
          toast.success(formatMessage('tool_released_success'));
        }
      })
      .catch((err: any) => {
        this.releaseToolState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.releaseToolState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to send just the not-yet-reported scanned tools (Tool Scan popup's
  * Save, or the Keep Tool popup's Save for a new tray) - posts ToolBomUsageInfo (State = Start)
  * for exactly these tools against trayId first.
  * @param trayId: Tray Identifier the tools are being reported against
  * @param processId: Process Identifier
  * @param toolNumbers: Tool Management Numbers to report - if omitted, defaults to every
  * scanned tool not yet sent for this tray
  * @param factoryId: Optional FactoryId override - used by the Keep Tool flow, where the live
  * value may already be cleared by the time this fires (after the tray transition)
  * @param toolModel: Optional ToolModel override - same reasoning as factoryId
  */
  ReportIncrementalToolStart = (
    trayId: string,
    processId: number,
    toolNumbers?: string[],
    factoryId?: string,
    toolModel?: string
  ) => {
    const toolsToSend = (toolNumbers ?? this.scannedToolNumbers).filter(
      (tool) => !!tool && !this.sentScannedToolNumbers.includes(tool)
    );

    if (!trayId || toolsToSend.length === 0) return Promise.resolve();

    this.sentScannedToolNumbers = [
      ...this.sentScannedToolNumbers,
      ...toolsToSend.filter((tool) => !this.sentScannedToolNumbers.includes(tool)),
    ];

    // The cached FactoryId is only populated while the Tool BOM tab has been opened (and is
    // cleared when it unmounts), so it can be empty here - resolve it first instead of sending
    // the request with a blank FactoryId.
    const factoryIdReady: Promise<string> =
      factoryId || getToolBOMFactoryIdFromLocalStorage()
        ? Promise.resolve(factoryId || getToolBOMFactoryIdFromLocalStorage() || '')
        : this.GetFactoryIdByTenantIdService(Number(getTenantIdFromLocalStorage())).then(
            () => getToolBOMFactoryIdFromLocalStorage() || ''
          );

    return factoryIdReady
      .then((resolvedFactoryId) =>
        this.ToolBomUsageInfoService(
          this.BuildToolBomUsageDetails(
            [trayId],
            processId,
            0,
            ToolBomUsageStateEnum.Start,
            ToolBomUsageModeEnum.Single,
            toolsToSend,
            resolvedFactoryId,
            toolModel
          )
        )
      )
      .then(
        action((reportSucceeded: boolean) => {
          if (reportSucceeded) {
            return this.UpdateToolStatusByToolManagementNoService(toolsToSend, true);
          }
          this.unmarkToolsSent(toolsToSend);
          return undefined;
        })
      )
      .catch(
        action((err: any) => {
          // Any unexpected failure (e.g. resolving the FactoryId) - same rollback.
          this.unmarkToolsSent(toolsToSend);
          toast.error(formatMessage(err));
        })
      );
  };

  /**
  * This function is used to snapshot the current tray's scanned tools (and the FactoryId/
  * ToolModel they need to be reported with) right before the Tool BOM tab's left table
  * unmounts during the tray transition and clears both - so the "Keep Tool" popup's Save still
  * has valid values to report the kept tools with for the new tray.
  */
  snapshotForKeep = () => {
    this.lastTrayScannedToolNumbers = [...this.scannedToolNumbers];
    this.keepFactoryId = getToolBOMFactoryIdFromLocalStorage() || '';
    this.keepToolModel = this.selectedToolModel;
  };

  /**
  * This function is used to add the tools chosen in the Keep Tool popup to the next tray's own
  * tools (already loaded via switchTrayTools, so a paused next tray keeps what it already had).
  * @param keptToolNumbers: Tool Management Numbers the operator chose to keep
  * @returns the kept tools the next tray didn't already have - the ones that still need to be
  * reported against it
  */
  resetForNextTray = (keptToolNumbers: string[]): string[] => {
    const newKept = keptToolNumbers.filter(
      (tool) => !!tool && !this.scannedToolNumbers.includes(tool)
    );
    this.scannedToolNumbers = [...this.scannedToolNumbers, ...newKept];
    this.lastTrayScannedToolNumbers = [];
    return newKept;
  };

  /**
  * This function is used to switch the working tray in Single mode: stashes the tray being left
  * (tools + which are already saved) and loads the target tray's stash - a tray with no stash
  * starts empty, so the previous tray's tools are never sent with it. When there's no active
  * tray yet (first tray of the session), any tools already scanned attach to it instead.
  * @param toTrayId: Tray Identifier being switched to
  */
  switchTrayTools = (toTrayId: string) => {
    if (!toTrayId || toTrayId === this.activeTrayId) return;

    if (this.activeTrayId) {
      this.trayToolsMap = {
        ...this.trayToolsMap,
        [this.activeTrayId]: {
          scanned: [...this.scannedToolNumbers],
          sent: [...this.sentScannedToolNumbers],
        },
      };
    }

    const target = this.trayToolsMap[toTrayId];
    if (target) {
      this.scannedToolNumbers = [...target.scanned];
      this.sentScannedToolNumbers = [...target.sent];
      const { [toTrayId]: _removed, ...rest } = this.trayToolsMap;
      this.trayToolsMap = rest;
    } else if (this.activeTrayId) {
      this.scannedToolNumbers = [];
      this.sentScannedToolNumbers = [];
      this.scannedToolMainUnitNumbers = [];
      this.applicatorMainUnitMap = {};
    }
    this.activeTrayId = toTrayId;
  };

  /**
  * This function is used to drop a tray's tools once it's removed/completed - if it was the
  * active tray, the live lists are emptied too, ready for the next tray to load.
  * @param trayId: Tray Identifier being removed
  */
  removeTrayTools = (trayId: string) => {
    if (!trayId) return;
    if (this.trayToolsMap[trayId]) {
      const { [trayId]: removed, ...rest } = this.trayToolsMap;
      this.trayToolsMap = rest;
    }
    if (this.activeTrayId === trayId) {
      this.activeTrayId = '';
      this.scannedToolNumbers = [];
      this.sentScannedToolNumbers = [];
      this.scannedToolMainUnitNumbers = [];
      this.applicatorMainUnitMap = {};
    }
  };

  /**
  * This function is used to record tools as already reported (e.g. sent inline with a Start
  * request) so a later Save/Start doesn't send them again.
  * @param toolNumbers: Tool Management Numbers already reported
  */
  markToolsSent = (toolNumbers: string[]) => {
    this.sentScannedToolNumbers = [
      ...this.sentScannedToolNumbers,
      ...toolNumbers.filter((tool) => !!tool && !this.sentScannedToolNumbers.includes(tool)),
    ];
  };

  /**
  * This function is used to roll back markToolsSent / the optimistic "sent" flag when the report
  * for those tools failed (Bug fix B3) - so they show as unsaved again (removable in the Tool Scan
  * popup) and the next Save/Start sends them again instead of silently skipping them.
  * @param toolNumbers: Tool Management Numbers whose report failed
  */
  unmarkToolsSent = (toolNumbers: string[]) => {
    this.sentScannedToolNumbers = this.sentScannedToolNumbers.filter(
      (tool) => !toolNumbers.includes(tool)
    );
  };

  /**
  * This function is used to clear all single-mode multi-tray tool tracking once the whole
  * queue of trays is done (the last tray has completed).
  */
  resetSessionToolTracking = () => {
    this.scannedToolNumbers = [];
    this.scannedToolMainUnitNumbers = [];
    this.applicatorMainUnitMap = {};
    this.sentScannedToolNumbers = [];
    this.allSessionScannedToolNumbers = [];
    this.lastTrayScannedToolNumbers = [];
    this.keepFactoryId = '';
    this.keepToolModel = '';
    this.activeTrayId = '';
    this.trayToolsMap = {};
    this.batchTrayPriorTools = {};
  };

  /**
  * This function is used to build the ToolBomUsageInfo detail rows (the cartesian product of
  * trayIds x the currently scanned tool numbers) without sending them anywhere - callers embed
  * these rows directly into the Operator Start/Pause/Complete request body instead of making a
  * separate ToolManagement/ToolBomUsageInfo call.
  * @param trayIds: Tray Identifiers
  * @param processId: Process Identifier
  * @param duration: Duration in seconds
  * @param state: Start/Pause/Complete
  * @param mode: Single/Batch
  */
  BuildToolBomUsageDetails = (
    trayIds: string[],
    processId: number,
    duration: number,
    state: ToolBomUsageStateEnum,
    mode: ToolBomUsageModeEnum,
    toolNumbers?: string[],
    factoryIdOverride?: string,
    toolModelOverride?: string
  ): IToolBomUsageInfoDetail[] => {
    const validTrayIds = (trayIds || []).filter((trayId) => !!trayId);
    const toolsToReport = toolNumbers ?? this.scannedToolNumbers;
    if (validTrayIds.length === 0 || toolsToReport.length === 0) {
      return [];
    }

    const factoryId = factoryIdOverride || getToolBOMFactoryIdFromLocalStorage() || '';
    const toolModel = toolModelOverride ?? this.selectedToolModel;
    const operatorUserId = Number(getUserId()) || 0;

    return validTrayIds.flatMap((trayId) =>
      toolsToReport.map((toolManagementNo) => ({
        Id: 0,
        FactoryId: factoryId,
        TrayId: trayId,
        ProcessId: Number(processId) || 0,
        ToolManagementNo: toolManagementNo,
        ToolModel: toolModel,
        OperatorUserId: operatorUserId,
        Duration: duration,
        State: state,
        Mode: mode,
        CreatedBy: operatorUserId,
        UpdatedBy: operatorUserId,
      }))
    );
  };

  /**
  * This function is used to build the Batch mode ToolBomUsageInfo rows. On Start only the tools
  * the operator scanned are crossed with every tray (skipping a tray's own earlier tools, which
  * already have rows). On Pause/Complete each tray also sends its own earlier tools (fetched
  * when the tray was scanned) - never crossed with the other trays.
  * @param trayIds: Tray Identifiers in the batch
  * @param processId: Process Identifier
  * @param duration: Duration in seconds
  * @param state: Start/Pause/Complete
  * @param excludePriorTrayIds: trays whose earlier tools must not be sent (e.g. read-only,
  * already-completed trays - their usage rows are no longer in a state the update can match)
  */
  BuildBatchToolBomUsageDetails = (
    trayIds: string[],
    processId: number,
    duration: number,
    state: ToolBomUsageStateEnum,
    excludePriorTrayIds: string[] = [],
    // "trayId::toolNo" pairs whose Start row this batch already inserted
    alreadyStartedTools: string[] = []
  ): IToolBomUsageInfoDetail[] => {
    const scanned = this.scannedToolNumbers;
    const excluded = excludePriorTrayIds.map((trayId) => String(trayId || '').trim());
    return (trayIds || [])
      .map((trayId) => String(trayId || '').trim())
      .filter((trayId) => !!trayId)
      .flatMap((trayId) => {
        const prior = excluded.includes(trayId) ? [] : this.batchTrayPriorTools[trayId] ?? [];
        if (state === ToolBomUsageStateEnum.Start) {
          return this.BuildToolBomUsageDetails(
            [trayId],
            processId,
            duration,
            state,
            ToolBomUsageModeEnum.Batch,
            scanned.filter(
              (tool) => !prior.includes(tool) && !alreadyStartedTools.includes(`${trayId}::${tool}`)
            )
          );
        }
        const scannedRows = this.BuildToolBomUsageDetails(
          [trayId],
          processId,
          duration,
          state,
          ToolBomUsageModeEnum.Batch,
          scanned
        );
        // ToolModel is left blank for the earlier tools (their own model isn't known here) - the
        // update keeps the stored model when it's empty.
        const priorRows = this.BuildToolBomUsageDetails(
          [trayId],
          processId,
          duration,
          state,
          ToolBomUsageModeEnum.Batch,
          prior.filter((tool) => !scanned.includes(tool)),
          undefined,
          ''
        );
        return [...scannedRows, ...priorRows];
      });
  };

  /**
  * This function is used to fetch the tools already recorded against a tray/process (in any
  * mode) with one GetToolMgtNumberByTrayAndProceesId call and keep them locally for the Batch
  * mode Pause/Complete request - a tray may have been paused earlier in either mode.
  * @param trayId: Tray Identifier
  * @param processId: Process Identifier
  * @param tenantId: Tenant Identifier - resolves FactoryId first when it isn't cached
  */
  FetchBatchTrayPriorTools = (trayId: string, processId: number, tenantId: number) => {
    if (!trayId) return Promise.resolve();

    // mode=0 asks the API for the tray's tools in every mode with a single call.
    const ALL_MODES = 0;
    const fetchAllModes = (): Promise<string[]> => {
      const factoryId = getToolBOMFactoryIdFromLocalStorage() || '';
      const url = `${URLConstants.GetToolMgtNumberByTrayAndProceesId}?factoryId=${encodeURIComponent(factoryId)}&trayId=${encodeURIComponent(trayId)}&processId=${encodeURIComponent(String(processId))}&mode=${ALL_MODES}`;
      return baseService
        .getRequest(url)
        .then((response: IApiResponse<IApiSuccessResponse<string[]>>) =>
          response.data.Error ? [] : response.data.Data || []
        )
        .catch(() => [] as string[]);
    };

    const factoryIdReady: Promise<void> = getToolBOMFactoryIdFromLocalStorage()
      ? Promise.resolve()
      : this.GetFactoryIdByTenantIdService(tenantId);

    return factoryIdReady
      .then(fetchAllModes)
      .then(
        action((tools: string[]) => {
          const unique = Array.from(new Set(tools.filter((tool) => !!tool)));
          this.batchTrayPriorTools = { ...this.batchTrayPriorTools, [trayId]: unique };
        })
      );
  };

  ReportToolBomUsage = (
    trayIds: string[],
    processId: number,
    duration: number,
    state: ToolBomUsageStateEnum,
    mode: ToolBomUsageModeEnum
  ) => {
    const details = this.BuildToolBomUsageDetails(
      trayIds,
      processId,
      duration,
      state,
      mode
    );
    if (details.length === 0) return Promise.resolve();

    return this.ToolBomUsageInfoService(details);
  };

  /**
  * @returns Promise resolving to whether THIS call succeeded (nothing to send counts as success) -
  * callers must use this rather than the shared addToolBomUsageInfoState, which overlapping calls reset.
  */
  ToolBomUsageInfoService = (details: IToolBomUsageInfoDetail[]): Promise<boolean> => {
    if (!details || details.length === 0) return Promise.resolve(true);

    this.addToolBomUsageInfoState.inProgress = true;
    this.addToolBomUsageInfoState.error = '';
    this.addToolBomUsageInfoState.success = false;
    let succeeded = false;
    return baseService
      .postRequest(URLConstants.ToolBomUsageInfo, {
        ToolBomUsageInfoDetailss: details,
      })
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addToolBomUsageInfoState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.addToolBomUsageInfoState.success = true;
          succeeded = true;
        }
      })
      .catch((err: any) => {
        this.addToolBomUsageInfoState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.addToolBomUsageInfoState.inProgress = false;
        })
      )
      .then(() => succeeded);
  };

  /**
  * This function is used to mark every tool management number the operator used during a
  * process as InUse - called once the Operator Start or Complete call has succeeded (Pause
  * doesn't call this). On Start (isStart = true) the API only updates Usage Status; on
  * Complete it also derives and updates Inspection Status from the tool's Inspection Cycle.
  * @param toolManagementNo: Tool Management Numbers used in the process
  * @param isStart: true when called on Start, false when called on Complete
  */
  UpdateToolStatusByToolManagementNoService = (toolManagementNo: string[], isStart: boolean) => {
    const validToolNumbers = (toolManagementNo || []).filter((toolNo) => !!toolNo);
    if (validToolNumbers.length === 0) return Promise.resolve();

    this.updateToolStatusByToolManagementNoState.inProgress = true;
    return baseService
      .postRequest(URLConstants.UpdateToolStatusByToolManagementNo, {
        ToolManagementNo: validToolNumbers,
        IsStart: isStart,
      })
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.updateToolStatusByToolManagementNoState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.updateToolStatusByToolManagementNoState.success = true;
        }
      })
      .catch((err: any) => {
        this.updateToolStatusByToolManagementNoState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.updateToolStatusByToolManagementNoState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to merge tool management numbers (fetched from a paused tray's usage
  * history) into the Tool Scan popup's scanned list, alongside whatever the operator already
  * has locally selected. Fetched tools are already recorded in tool_usage_info, so they're also
  * marked as sent - only newly scanned tools need to go out via ReportIncrementalToolStart;
  * Complete/Pause still send the whole merged list regardless of this flag.
  * @param toolNumbers: Tool Management Numbers to merge in
  */
  mergeScannedTools = (toolNumbers: string[]) => {
    const newTools = toolNumbers.filter(
      (tool) => !!tool && !this.scannedToolNumbers.includes(tool)
    );
    if (newTools.length === 0) return;

    this.scannedToolNumbers = [...this.scannedToolNumbers, ...newTools];
    this.sentScannedToolNumbers = [
      ...this.sentScannedToolNumbers,
      ...newTools.filter((tool) => !this.sentScannedToolNumbers.includes(tool)),
    ];
    this.allSessionScannedToolNumbers = [
      ...this.allSessionScannedToolNumbers,
      ...newTools.filter((tool) => !this.allSessionScannedToolNumbers.includes(tool)),
    ];
  };

  /**
  * This function is used to merge a tray's already-saved tools into that tray's own list - the
  * live list when it's still the active tray, otherwise its stash (the operator may have
  * switched to another tray while the fetch was in flight).
  * @param trayId: Tray Identifier the tools were fetched for
  * @param toolNumbers: Tool Management Numbers to merge in
  */
  mergeTrayTools = (trayId: string, toolNumbers: string[]) => {
    if (!this.activeTrayId || this.activeTrayId === trayId) {
      this.mergeScannedTools(toolNumbers);
      return;
    }

    const entry = this.trayToolsMap[trayId] ?? { scanned: [], sent: [] };
    const newTools = toolNumbers.filter((tool) => !!tool && !entry.scanned.includes(tool));
    if (newTools.length === 0) return;
    this.trayToolsMap = {
      ...this.trayToolsMap,
      [trayId]: {
        scanned: [...entry.scanned, ...newTools],
        sent: [...entry.sent, ...newTools.filter((tool) => !entry.sent.includes(tool))],
      },
    };
    this.allSessionScannedToolNumbers = [
      ...this.allSessionScannedToolNumbers,
      ...newTools.filter((tool) => !this.allSessionScannedToolNumbers.includes(tool)),
    ];
  };

  /**
  * This function is used to fetch the tool management numbers already recorded against a tray/
  * process (any state - Start, Pause or Complete) via GetToolMgtNumberByTrayAndProceesId, and
  * merge them into the Tool Scan popup's scanned list - used when reopening a tray whose process
  * is paused, so the popup shows what was already scanned before the pause.
  * @param trayId: Tray Identifier
  * @param processId: Process Identifier
  * @param mode: Single/Batch
  * @param tenantId: Tenant Identifier - resolves FactoryId first when it isn't already cached
  * (this can fire before the operator ever opens the Tool BOM tab, which is what normally
  * populates it)
  */
  GetToolMgtNumberByTrayAndProceesIdService = (
    trayId: string,
    processId: number,
    mode: ToolBomUsageModeEnum,
    tenantId: number
  ) => {
    if (!trayId) return Promise.resolve();

    const fetchList = () => {
      this.getToolMgtNumberByTrayState.inProgress = true;
      this.getToolMgtNumberByTrayState.error = '';
      this.getToolMgtNumberByTrayState.success = false;
      const factoryId = getToolBOMFactoryIdFromLocalStorage() || '';
      const url = `${URLConstants.GetToolMgtNumberByTrayAndProceesId}?factoryId=${encodeURIComponent(factoryId)}&trayId=${encodeURIComponent(trayId)}&processId=${encodeURIComponent(String(processId))}&mode=${mode}`;
      return baseService
        .getRequest(url)
        .then((response: IApiResponse<IApiSuccessResponse<string[]>>) => {
          if (response.data.Error) {
            this.getToolMgtNumberByTrayState.error = response.data.Message;
          } else {
            this.mergeTrayTools(trayId, response.data.Data || []);
            this.getToolMgtNumberByTrayState.success = true;
          }
        })
        .catch((err: any) => {
          this.getToolMgtNumberByTrayState.error = err;
        })
        .finally(
          action(() => {
            this.getToolMgtNumberByTrayState.inProgress = false;
          })
        );
    };

    if (getToolBOMFactoryIdFromLocalStorage()) {
      return fetchList();
    }
    return this.GetFactoryIdByTenantIdService(tenantId).then(fetchList);
  };

  /**
  * This function is used to map the GetToolBOMInfoById response into the form-facing shape the Add/Edit form expects.
  * @param data: Tool BOM detail response data
  */
  mapToolBOMDetail = (data: IToolBOMInfo): IAddToolBOM => {
    return {
      Id: data.Id,
      MaterialProductId: data.MaterialProductId || '',
      ToolModel: data.ToolModel || '',
      MaterialKatashiki1: data.MaterialKatashiki1 || '',
      Category: data.Category || '',
      MaterialManufacturerCode: data.MaterialManufacturerCode || '',
      TecmibizCode: data.TecmibizCode || '',
    };
  };

  /**
  * This computed property is used to map toolBOMList to the list suitable for the Grid Component.
  */
  get getToolBOMList(): IToolBOMList[] {
    if (this.toolBOMList?.ToolBOM && this.toolBOMList.ToolBOM?.length > 0)
      return this.toolBOMList.ToolBOM.map((item) => {
        let isDelete = true;
        let isEdit = true;
        return {
          Id: item.Id,
          ToolId: item.ToolId,
          MaterialProductId: item.MaterialProductId,
          ToolModel: item.ToolModel,
          MaterialKatashiki1: item.MaterialKatashiki1,
          Category: item.Category,
          MaterialManufacturerCode: item.MaterialManufacturerCode,
          TecmibizCode: item.TecmibizCode,
          isDelete: isDelete,
          isEdit: isEdit,
          CreatedBy: item.CreatedBy,
          UpdatedBy: item.UpdatedBy,
        };
      });
    return [];
  }

  /**
  * This function is used to reset addUpdateToolBOMState observables to their initial values.
  */
  resetAddUpdateState = () => {
    this.addUpdateToolBOMState = { ...this.initialStateValue };
  };

  /**
  * This function is used to reset deleteToolBOMState observables to their initial values.
  */
  resetDeleteState = () => {
    this.deleteToolBOMState = { ...this.initialStateValue };
  };

  /**
  * This function is used to reset toolBOMDetail and getToolBOMByIdState observables to their initial values.
  */
  resetDetail = () => {
    this.toolBOMDetail = AddToolBOMInitialState;
    this.getToolBOMByIdState = { ...this.initialStateValue };
  };

  /**
  * This function is used to reset fileUploadState and importResults observables to their initial values.
  */
  resetFileUploadState = () => {
    this.fileUploadState = { ...this.initialStateValue };
    this.importResults = [];
  };

  /**
  * This function is used to reset toolBomRelationInfoList and getToolBomRelationInfoState observables to their initial values.
  */
  resetToolBomRelationInfo = () => {
    this.toolBomRelationInfoList = [];
    this.getToolBomRelationInfoState = { ...this.initialStateValue };
  };

  /**
  * This function is used to reset toolBomToolManagementList and getToolBomToolManagementListState observables to their initial values.
  */
  resetToolBomToolManagementList = () => {
    this.toolBomToolManagementList = [];
    this.getToolBomToolManagementListState = { ...this.initialStateValue };
    this.selectedToolModel = '';
  };

  /**
  * This function is used to reset only the Tool BOM list displayed in the UI.
  */
  resetToolBOMList = () => {
    this.toolBOMList = getToolBOMListInitialState;
  };

  /**
  * This function is used to reset all store observables to their initial values.
  */
  resetStore = () => {
    this.toolBOMList = getToolBOMListInitialState;
    this.addUpdateToolBOMState = { ...this.initialStateValue };
    this.deleteToolBOMState = { ...this.initialStateValue };
    this.getAllToolBOMState = { ...this.initialStateValue };
    this.toolBOMDetail = AddToolBOMInitialState;
    this.getToolBOMByIdState = { ...this.initialStateValue };
    this.getFactoryIdState = { ...this.initialStateValue };
    this.fileUploadState = { ...this.initialStateValue };
    this.importResults = [];
    this.toolBomRelationInfoList = [];
    this.getToolBomRelationInfoState = { ...this.initialStateValue };
    this.toolBomToolManagementList = [];
    this.getToolBomToolManagementListState = { ...this.initialStateValue };
    this.selectedToolModel = '';
    this.scannedToolNumbers = [];
    this.scannedToolMainUnitNumbers = [];
    this.applicatorMainUnitMap = {};
    this.sentScannedToolNumbers = [];
    this.allSessionScannedToolNumbers = [];
    this.lastTrayScannedToolNumbers = [];
    this.keepFactoryId = '';
    this.keepToolModel = '';
    this.activeTrayId = '';
    this.trayToolsMap = {};
    this.batchTrayPriorTools = {};
    this.addToolBomUsageInfoState = { ...this.initialStateValue };
    this.updateToolStatusByToolManagementNoState = { ...this.initialStateValue };
    this.releaseToolState = { ...this.initialStateValue };
    this.getToolMgtNumberByTrayState = { ...this.initialStateValue };
  };
}

export default new ToolBOMStore();
